import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MedicalCertificate,
  MedicalOrder,
  RequestType,
  requestTypeFromUrlParser,
  PatientAttributes,
  requestTypeExtendedTranslation,
} from "../../interfaces";
import {
  FullScreenLoader,
  NavigateBeforeButton,
  FormHeader,
  FormContentContainer,
  FormBody,
} from "../../components";
import { GeneralInfoCard, MedicalDocBottomActionArea } from "./components";
import { useDocument } from "./hooks/useDocument";
import { generatePdfName } from "../../utils";
import Tracker from "@openreplay/tracker";
import { SHOULD_TRACK_SESSION, tracker } from "@utils/openreplay";

export const DocumentValidationScreen: React.FC = () => {
  const { documentType: documentTypeFromUrl } = useParams<{
    documentType: RequestType;
  }>();
  const documentType = documentTypeFromUrl
    ? requestTypeFromUrlParser[documentTypeFromUrl]
    : undefined;
  const { data, isFetching } = useDocument();
  const navigate = useNavigate();
  const handleBack = useCallback(() => {
    navigate(`/app/verificacion/${documentTypeFromUrl}`);
  }, [navigate, documentTypeFromUrl]);

  useEffect(() => {
    if (!data && !isFetching) {
      handleBack();
    }
  }, [data, isFetching, handleBack]);

  useEffect(() => {
    if (!data) {
      if (SHOULD_TRACK_SESSION && tracker) {
        identifyOpenReplaySession(null, tracker);
      }
      return;
    }
    const { patient } = data;
    if (SHOULD_TRACK_SESSION && tracker) {
      identifyOpenReplaySession(patient, tracker);
    }
  }, [data]);

  if (isFetching)
    return (
      <>
        <FormHeader
          title={`Validación de ${
            documentType
              ? requestTypeExtendedTranslation[documentType]
              : "Documento"
          }`}
          titleColor="white"
          left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
        />
        <FormContentContainer flex={1} disableGutters>
          <FormBody>
            <FullScreenLoader title="Cargando información..." />
          </FormBody>
        </FormContentContainer>
      </>
    );

  if (!data) {
    return null;
  }

  const {
    medicalDocument: document,
    medicalDocumentPDFReadUrl: documentPDFReadUrl,
    patient,
  } = data;

  const documentPDFName = generatePdfName({
    patient,
    documentType: documentType as RequestType,
  });

  return (
    <>
      <FormHeader
        title={`Validación de ${
          documentType
            ? requestTypeExtendedTranslation[documentType]
            : "Documento"
        }`}
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          {documentType === RequestType.MEDICAL_CERTIFICATE && (
            <GeneralInfoCard
              documentType={documentType}
              emissionDate={
                new Date((document as MedicalCertificate).effectiveDate)
              }
              patientData={patient}
              medicalDocumentPDFReadUrl={documentPDFReadUrl}
            />
          )}

          {documentType === RequestType.MEDICAL_ORDER && (
            <GeneralInfoCard
              documentType={documentType}
              emissionDate={new Date((document as MedicalOrder).effectiveDate)}
              patientData={patient}
              medicalDocumentPDFReadUrl={documentPDFReadUrl}
            />
          )}
          <MedicalDocBottomActionArea
            medicalDocumentPDFData={{
              medicalDocumentPDFReadUrl: documentPDFReadUrl,
              medicalDocumentPDFName: documentPDFName,
            }}
          />
        </FormBody>
      </FormContentContainer>
    </>
  );
};

function identifyOpenReplaySession(
  patient: PatientAttributes | null,
  tracker: Tracker | null
) {
  if (!tracker) return;
  if (patient) {
    tracker.setUserID("Sesión de verificación de documentos");
    tracker.setMetadata("name", "Sesión de verificación de documentos");
    tracker.setMetadata("patientName", patient.name);
    tracker.setMetadata("patientDocumentNumber", patient.documentNumber);
    tracker.setMetadata("patientUuid", patient.uuid);
  } else {
    tracker.setUserID("Sesión de verificación de documentos");
    tracker.setMetadata("name", "Sesión de verificación de documentos");
  }
}
